import React from 'react';
import { COLORS, BUTTON_TOKENS, BORDER_RADIUS } from '../../../styles/UISystem';

/**
 * ButtonRounded Component
 *
 * A standardized rounded button component with consistent styling.
 *
 * Sizes:
 * - 'full': 100% width - Single primary action anchoring the bottom of an overlay card
 * - 'large' (default): 280px - Multiple buttons in an overlay, or standalone CTA
 * - 'auto': auto - Compact inline button alongside other UI elements
 * - 'compact': auto width, ~36px height - Content-sized for tight layouts (WelcomeOverlay "Open")
 * - 'inline': no height/padding/background - Tappable text, use with variant="blank"
 *
 * Variants:
 * - 'primary' (default): Black background (#000000), white text - main actions
 * - 'blue': iOS blue background (#007AFF), white text - high-value conversion actions
 * - 'secondary': White background, gray text (#6B6B6B) - alternative actions
 * - 'black': Black background, white text - high contrast for busy contexts
 * - 'blank': Transparent, gray text (#6B6B6B), no shadow - de-emphasized (skip, remind later)
 *
 * @param {string} text - Button text
 * @param {function} icon - Optional Phosphor icon component rendered before the
 *   text (the 2026-08-18 Detail frames put a download glyph in "Save to
 *   contacts"). Pass the component, not an element.
 * @param {function} onClick - Click handler function
 * @param {string} size - Button size ('full', 'large', 'auto', 'compact', 'inline')
 * @param {string} variant - Button variant ('primary', 'secondary', 'black', 'blank')
 * @param {boolean} disabled - Whether the button is disabled
 * @param {string} width - DEPRECATED: Use size prop instead
 */
export const ButtonRounded = ({
  text = 'Button',
  icon: Icon = null,
  onClick,
  size = 'large',
  variant = 'primary',
  disabled = false,
  width, // Deprecated - kept for backwards compatibility
  style: styleProp,
  ...props
}) => {
  // Size mapping (width)
  const sizeMap = {
    full: '100%',
    large: '280px',
    auto: 'auto',
    compact: 'auto',
    inline: 'auto',
  };

  // Handle deprecated width prop with mapping to size
  let resolvedWidth = sizeMap[size];
  if (width !== undefined) {
    // Map old width values to new size system
    if (width === '100%') {
      resolvedWidth = '100%';
    } else if (width === 'auto') {
      resolvedWidth = 'auto';
    } else {
      // For percentage widths like '60%', '70%', use large (280px)
      resolvedWidth = '280px';
    }
  }

  // Handle deprecated 'optional' variant (alias for 'secondary')
  const resolvedVariant = variant === 'optional' ? 'secondary' : variant;

  const standardShadow = BUTTON_TOKENS.ACTION_SHADOW;

  // Determine styles based on variant and disabled state
  let backgroundColor;
  let textColor;
  let boxShadow;
  let fontWeight;
  let fontSize = '18px';

  if (disabled) {
    backgroundColor = BUTTON_TOKENS.ACTION_BG_DISABLED;
    textColor = BUTTON_TOKENS.ACTION_TEXT_DISABLED;
    boxShadow = 'none';
    fontWeight = '600';
  } else if (resolvedVariant === 'primary') {
    backgroundColor = BUTTON_TOKENS.ACTION_BG_PRIMARY;
    textColor = BUTTON_TOKENS.ACTION_TEXT_PRIMARY;
    boxShadow = standardShadow;
    fontWeight = '400';
  } else if (resolvedVariant === 'blue') {
    backgroundColor = COLORS.PRIMARY_BLUE;
    textColor = '#FFFFFF';
    boxShadow = standardShadow;
    fontWeight = '600';
  } else if (resolvedVariant === 'secondary') {
    backgroundColor = '#F9F9F8';
    textColor = BUTTON_TOKENS.ACTION_TEXT_SECONDARY;
    boxShadow = standardShadow;
    fontWeight = '500';
  } else if (resolvedVariant === 'black') {
    backgroundColor = BUTTON_TOKENS.ACTION_BG_PRIMARY;
    textColor = BUTTON_TOKENS.ACTION_TEXT_PRIMARY;
    boxShadow = standardShadow;
    fontWeight = '600';
  } else if (resolvedVariant === 'blank') {
    backgroundColor = 'transparent';
    textColor = BUTTON_TOKENS.ACTION_TEXT_SECONDARY;
    boxShadow = 'none';
    fontWeight = '400';
    fontSize = '16px';
  }

  // Size overrides for compact and inline
  if (size === 'compact') {
    fontSize = '16px';
  } else if (size === 'inline') {
    backgroundColor = 'transparent';
    boxShadow = 'none';
    if (!disabled) {
      fontSize = '16px';
    }
  }

  // Height and padding by size
  const isCompact = size === 'compact';
  const isInline = size === 'inline';
  const height = isInline ? 'auto' : isCompact ? 'auto' : '48px';
  const padding = isInline ? '0' : isCompact ? '8px 20px' : '0 24px';

  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        height,
        backgroundColor,
        borderRadius: isInline ? 0 : BORDER_RADIUS.PILL,
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow,
        display: isInline ? 'inline' : 'flex',
        justifyContent: isInline ? undefined : 'center',
        alignItems: isInline ? undefined : 'center',
        width: isInline ? 'auto' : resolvedWidth,
        padding,
        color: textColor,
        fontWeight,
        fontSize,
        whiteSpace: 'nowrap',
        transition: 'background-color 0.2s ease',
        ...styleProp,
      }}
      {...props}
    >
      {Icon ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icon size={20} weight="regular" />
          {text}
        </span>
      ) : (
        text
      )}
    </button>
  );
};

/**
 * ButtonRoundedExample Component
 *
 * Example usage of the ButtonRounded component showing all sizes and variants.
 */
export const ButtonRoundedExample = () => {
  const handleClick = () => {
    console.log('ButtonRounded clicked');
  };

  return (
    <div style={{ marginBottom: '60px' }}>
      <div
        style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: COLORS.DARK_GRAY,
          marginBottom: '32px',
          textAlign: 'center',
        }}
      >
        ButtonRounded
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '32px',
        }}
      >
        {/* Sizes */}
        <div style={{ width: '100%' }}>
          <div
            style={{
              fontSize: '18px',
              fontWeight: '600',
              color: COLORS.DARK_GRAY,
              marginBottom: '16px',
              textAlign: 'center',
            }}
          >
            Sizes
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <ButtonRounded text="Full (100%)" onClick={handleClick} size="full" />
            <ButtonRounded text="Large (280px)" onClick={handleClick} size="large" />
            <ButtonRounded text="Auto" onClick={handleClick} size="auto" />
            <ButtonRounded text="Compact (~36px)" onClick={handleClick} size="compact" />
            <div>
              <ButtonRounded
                text="Inline text"
                onClick={handleClick}
                size="inline"
                variant="blank"
              />
            </div>
          </div>
        </div>

        {/* Primary variant */}
        <div style={{ width: '100%' }}>
          <div
            style={{
              fontSize: '16px',
              color: COLORS.DARK_GRAY,
              marginBottom: '12px',
              textAlign: 'center',
            }}
          >
            Primary (Black - Main Actions)
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <ButtonRounded text="Share" onClick={handleClick} variant="primary" />
            <ButtonRounded text="Share" onClick={handleClick} variant="primary" disabled />
          </div>
        </div>

        {/* Secondary variant */}
        <div style={{ width: '100%' }}>
          <div
            style={{
              fontSize: '16px',
              color: COLORS.DARK_GRAY,
              marginBottom: '12px',
              textAlign: 'center',
            }}
          >
            Secondary (White - Alternative Actions)
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <ButtonRounded text="Add contact" onClick={handleClick} variant="secondary" />
            <ButtonRounded text="Add contact" onClick={handleClick} variant="secondary" disabled />
          </div>
        </div>

        {/* Black variant */}
        <div style={{ width: '100%' }}>
          <div
            style={{
              fontSize: '16px',
              color: COLORS.DARK_GRAY,
              marginBottom: '12px',
              textAlign: 'center',
            }}
          >
            Black (High Contrast)
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <ButtonRounded text="Text the link" onClick={handleClick} variant="black" />
            <ButtonRounded text="Text the link" onClick={handleClick} variant="black" disabled />
          </div>
        </div>

        {/* Blank variant */}
        <div style={{ width: '100%' }}>
          <div
            style={{
              fontSize: '16px',
              color: COLORS.DARK_GRAY,
              marginBottom: '12px',
              textAlign: 'center',
            }}
          >
            Blank (De-emphasized)
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <ButtonRounded text="Remind me later" onClick={handleClick} variant="blank" />
            <ButtonRounded text="Receive only" onClick={handleClick} variant="blank" />
          </div>
        </div>
      </div>
    </div>
  );
};
